import React, { ComponentType, useState } from 'react';
import {
    LuActivity,
    LuArrowLeft,
    LuCalendar,
    LuFileText,
    LuFileType,
    LuLoader,
    LuMic,
    LuPen,
    LuTrash2,
    LuTrendingUp,
} from 'react-icons/lu';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { Page } from '@/components/ui/page';
import { convertBackendTimeToBrowser, formatDate, useRequiredParams } from 'utils';
import { useHouseholdTeamNameResolver } from 'views/admin/tenantTeam/tenant-team-queries';
import { getCalendarEventType } from 'views/calendar/calendar-event-type';
import { formatEventTimeRange, hasNoteSections } from 'views/calendar/meeting-minutes-list-utils';
import { MeetingMinutesNoteSections } from 'views/calendar/widgets/MeetingMinutesNoteSections';
import { NewMeetingMinutesSuggestions, SuggestionsSkeleton } from 'views/calendar/widgets/NewMeetingMinutesSuggestions';
import { EventDetailsDrawerByGuid } from 'views/household/HouseholdEvents/EventDetailsDrawer';
import { useGetEvent, useGetMeetingBot } from 'views/household/HouseholdEvents/events-queries';
import { ApplyNoteTemplateDialog } from 'views/household/HouseholdNotes/ApplyNoteTemplateDialog';
import { getNotePersonName } from 'views/household/HouseholdNotes/note-utils';

import { useAdvisorLogin, useGlobalAccount } from '../household-queries';
import { DeleteHouseholdNoteDialog } from './DeleteHouseholdNoteDialog';
import { NewNoteBodyCard } from './NewNoteBodyCard';
import {
    useApplyTemplateToNoteMutation,
    useCreatePortfolioFromTranscript,
    useMeetingTranscript,
    useNoteQuery,
} from './note-queries';
import { NoteTranscriptTabs } from './NoteTranscriptTabs';

export const NewHouseholdNotePage = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const noteGUID = searchParams.get('noteGUID') ?? '';
    const [isApplyNoteTemplateDialogOpen, setIsApplyNoteTemplateDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isEventOpen, setIsEventOpen] = useState(false);

    const globalAccountQuery = useGlobalAccount(globalAccountGUID);
    const noteQuery = useNoteQuery(noteGUID);
    const { nameFor } = useHouseholdTeamNameResolver(globalAccountGUID);
    const createPortfolioMutation = useCreatePortfolioFromTranscript(globalAccountGUID, noteGUID);
    const advisorLoginMutation = useAdvisorLogin();
    const applyTemplateMutation = useApplyTemplateToNoteMutation(globalAccountGUID, noteGUID);

    const meetingBotQuery = useGetMeetingBot(
        globalAccountGUID,
        noteQuery.data?.eventGUID ?? '',
        !!noteQuery.data?.eventGUID,
    );
    const transcript = useMeetingTranscript(noteQuery.data?.meetingBotGUID);
    const eventQuery = useGetEvent(noteQuery.data?.eventGUID ?? null);

    if (globalAccountQuery.isLoading || noteQuery.isLoading) {
        return <NewHouseholdNotePageSkeleton />;
    }

    if (!noteQuery.data || !globalAccountQuery.data) {
        return (
            <Page>
                <div className="text-muted-foreground flex min-h-0 flex-1 flex-col items-center justify-center py-8 text-center">
                    <LuFileText className="mx-auto mb-4 h-12 w-12 opacity-50" />
                    <p>This note was deleted or you no longer have access.</p>
                    <Button asChild variant="outline" className="mt-4">
                        <Link to="../notes">Back to notes</Link>
                    </Button>
                </div>
            </Page>
        );
    }

    const note = noteQuery.data;
    const personName = getNotePersonName(note.createdBy, nameFor);
    const updatedPersonName = note.updatedBy ? getNotePersonName(note.updatedBy, nameFor) : personName;
    const isMeetingBotProcessing = Boolean(note.meetingBotGUID && meetingBotQuery.data?.status === 'PROCESSING');
    const event = eventQuery.data;
    const eventStart = event && convertBackendTimeToBrowser(event.startTime, event.startTimeZone);

    return (
        <Page>
            <main className={MAIN_CLASSES}>
                <div className="flex items-start gap-3">
                    <Button asChild variant="ghost" size="icon" aria-label="Back to notes">
                        <Link to="../notes">
                            <LuArrowLeft className="h-5 w-5" />
                        </Link>
                    </Button>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <h2 className="text-cocoa-1000 text-2xl font-bold break-words">
                            {note.subject || 'Untitled Note'}
                        </h2>
                        <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
                            {event && (
                                <>
                                    <span>
                                        {getCalendarEventType(event) === 'client'
                                            ? 'Client meeting'
                                            : 'Internal meeting'}
                                    </span>
                                    <span aria-hidden>·</span>
                                    <span className="tabular-nums">
                                        {`${formatDate(eventStart, 'EEE, MMM d')} · ${formatEventTimeRange(event)}`}
                                    </span>
                                </>
                            )}
                            {note.meetingBotGUID && (
                                <Badge
                                    variant="outline"
                                    className="bg-seafoam-100 text-cocoa-1000 gap-1.5 border-transparent"
                                >
                                    <LuMic className="h-3 w-3" /> Recorded by Claire&trade;
                                </Badge>
                            )}
                        </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <Button asChild variant="outline">
                            <Link to={`../notes/edit?noteGUID=${noteGUID}`}>
                                <LuPen /> <span>Edit note</span>
                            </Link>
                        </Button>
                        <DropdownMenu>
                            <EllipsisDropdownMenuTrigger ariaLabel="Note actions" />
                            <DropdownMenuContent align="end">
                                {note.meetingBotGUID && (
                                    <DropdownMenuItem
                                        onClick={() => setIsApplyNoteTemplateDialogOpen(true)}
                                        disabled={applyTemplateMutation.isPending}
                                    >
                                        <MenuItemIcon icon={LuFileType} isPending={applyTemplateMutation.isPending} />
                                        Apply note template
                                    </DropdownMenuItem>
                                )}
                                {note.meetingBotGUID && !note.accountGroupGUID && (
                                    <DropdownMenuItem
                                        onClick={() => createPortfolioMutation.mutate(note.meetingBotGUID ?? '')}
                                        disabled={createPortfolioMutation.isPending}
                                    >
                                        <MenuItemIcon
                                            icon={LuTrendingUp}
                                            isPending={createPortfolioMutation.isPending}
                                        />
                                        Convert to financial plan
                                    </DropdownMenuItem>
                                )}
                                {note.accountGroupGUID && (
                                    <DropdownMenuItem
                                        onClick={() =>
                                            advisorLoginMutation.mutate({
                                                householdGUID: globalAccountGUID,
                                                redirect: `/lab/${note.accountGroupGUID}`,
                                            })
                                        }
                                        disabled={advisorLoginMutation.isPending}
                                    >
                                        <MenuItemIcon icon={LuActivity} isPending={advisorLoginMutation.isPending} />
                                        Analyze in Portfolio Lab&#8482;
                                    </DropdownMenuItem>
                                )}
                                {note.eventGUID && (
                                    <DropdownMenuItem onClick={() => setIsEventOpen(true)}>
                                        <MenuItemIcon icon={LuCalendar} />
                                        Open calendar event
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setIsDeleteDialogOpen(true)}>
                                    <MenuItemIcon icon={LuTrash2} />
                                    Delete note
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {/* Two-column layout: note on the left, Claire suggestions on the right */}
                <div className="flex flex-1 gap-4 overflow-hidden">
                    {/* Left column: Note content */}
                    <div className="flex w-0 flex-1 flex-col gap-4 overflow-y-auto">
                        <NoteTranscriptTabs transcript={transcript}>
                            <NewNoteBodyCard
                                note={note}
                                personName={personName}
                                updatedPersonName={updatedPersonName}
                                summary={hasNoteSections(note) && <MeetingMinutesNoteSections note={note} />}
                            />
                        </NoteTranscriptTabs>
                    </div>

                    {/* Right column: Claire suggestions and created hub items */}
                    <div className="flex w-[480px] flex-shrink-0 flex-col gap-4 overflow-y-auto">
                        <NewMeetingMinutesSuggestions
                            note={note}
                            householdGUID={globalAccountGUID}
                            isProcessing={isMeetingBotProcessing}
                        />
                    </div>
                </div>
            </main>

            <ApplyNoteTemplateDialog
                globalAccountGUID={globalAccountGUID}
                noteGUID={note.noteGUID}
                isOpen={isApplyNoteTemplateDialogOpen}
                setIsOpen={setIsApplyNoteTemplateDialogOpen}
            />
            <DeleteHouseholdNoteDialog
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                note={note}
                onSuccess={() => navigate('../notes')}
            />
            {/* Mounted only while open, like the suggestion rows: the drawer fetches its event on mount. */}
            {isEventOpen && note.eventGUID && (
                <EventDetailsDrawerByGuid eventGUID={note.eventGUID} isOpen onOpenChange={setIsEventOpen} />
            )}
        </Page>
    );
};

const MAIN_CLASSES = 'flex flex-col gap-4 overflow-hidden p-6 pb-8';

// A menu item's leading icon, swapped for a spinner while the item's mutation runs.
const MenuItemIcon = ({
    icon: Icon,
    isPending,
}: {
    icon: ComponentType<{ className?: string }>;
    isPending?: boolean;
}) => (isPending ? <LuLoader className="mr-2 h-4 w-4 animate-spin" /> : <Icon className="mr-2 h-4 w-4" />);

// Mirrors the page layout (back link, header meta, note card, rail cards) so nothing shifts when
// the note arrives. The rail cards keep their own skeletons for their later-loading queries.
const NewHouseholdNotePageSkeleton = () => (
    <Page>
        <main className={MAIN_CLASSES}>
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-gray-200" />
                <div className="flex flex-1 flex-col gap-1">
                    <div className="h-8 w-1/2 animate-pulse rounded bg-gray-200" />
                    <div className="flex items-center gap-2">
                        <div className="h-4 w-72 animate-pulse rounded bg-gray-200" />
                        <div className="h-6 w-36 animate-pulse rounded-full bg-gray-200" />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="h-10 w-28 animate-pulse rounded-md bg-gray-200" />
                    <div className="h-8 w-8 animate-pulse rounded-md bg-gray-200" />
                </div>
            </div>
            <div className="flex flex-1 gap-4 overflow-hidden">
                <div className="flex w-0 flex-1 flex-col gap-4">
                    <div className="flex flex-col gap-3 rounded-lg border bg-white p-6">
                        <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
                        <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                        <div className="h-4 w-5/6 animate-pulse rounded bg-gray-200" />
                        <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
                    </div>
                </div>
                <div className="flex w-[480px] flex-shrink-0 flex-col gap-4">
                    <div className="bg-cocoa-0 flex flex-col gap-2 rounded-md p-3.5">
                        <div className="h-3 w-24 animate-pulse rounded bg-gray-200" />
                        <SuggestionsSkeleton />
                    </div>
                </div>
            </div>
        </main>
    </Page>
);
